import { useCallback, useMemo, useState } from 'react';
import { Filters, Option } from '../../../types';
import { DEFAULT_TABLE_FILTERS, REPORT_PAGE_SIZE_KEY } from '../constants';

interface UseTableFiltersProps<T> {
    defaultFilters: T;
    persistPageSize?: boolean;
}

export interface UseTableFiltersReturn<T> {
    updateFilters: (name: string, value: Option | Option[]) => void;
    setSorted: (sortOptions: Filters['sorted']) => void;
    setFilters: (prevState: T) => void;
    resetFilters: () => void;
    filters: T;
    setRowsPerPage: (rowsPerPage: number) => void;
    setPage: (page: number) => void;
}

export const useTableFilters = <T extends { [key: string]: any }>({
    defaultFilters,
    persistPageSize,
}: UseTableFiltersProps<T>) => {
    const reportPageSize = Number(localStorage.getItem(REPORT_PAGE_SIZE_KEY)) || DEFAULT_TABLE_FILTERS.pageSize;
    const initialFilters = useMemo(
        () => (persistPageSize ? { ...defaultFilters, pageSize: reportPageSize } : defaultFilters),
        [reportPageSize, defaultFilters, persistPageSize]
    );

    const [filters, setFilters] = useState<T>(initialFilters);

    const updateFilters = useCallback((name: string, value: Option | Option[]) => {
        setFilters((prevState) => ({ ...prevState, pageNumber: 0, [name]: value }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters(initialFilters);
    }, [initialFilters]);

    const setPage = useCallback(
        (page: number) => {
            updateFilters('pageNumber', page);
        },
        [updateFilters]
    );

    const setRowsPerPage = useCallback(
        (rowsPerPage: number) => {
            updateFilters('pageSize', rowsPerPage);

            if (persistPageSize) {
                localStorage.setItem(REPORT_PAGE_SIZE_KEY, rowsPerPage.toString());
            }
        },
        [updateFilters, persistPageSize]
    );

    const setSorted = useCallback(
        (sortOptions: Filters['sorted']) => {
            setFilters((prevState) => ({ ...prevState, sorted: sortOptions }));
        },
        [setFilters]
    );

    return { filters, updateFilters, resetFilters, setFilters, setPage, setRowsPerPage, setSorted };
};
